import { ResourceType } from './bos/resource.js'
import { ExecutionLocation } from './script/execution-context.js'
import type { ValidationErrors } from '@angular/forms'

import {
	EntityCommon,
	EntityInstance,
	EntityRecord,
	UserIdType,
} from './types.js'
import type {
	FindOptionsSelect,
	FindOptionsWhere,
	FindOptionsRelations,
	FindOptionsOrder,
} from 'typeorm'

export interface Choice<T = any> {
	id: T
	label: string | number
	$isOther?: boolean
}
export interface HasChoices {
	getChoices(...args: any[]): Choice[]
}

export type ValidationError = {
	error: string
	skipSubsequentRules?: boolean
	ruleId?: string
}

export type { EntityState } from './data/entity-state.js'
export type * from '@lg/support-services'
export type { SendMailOptions as MailObject } from 'nodemailer'

export type {
	CdkDragDrop,
	CdkDragEnd,
	CdkDragEnter,
	CdkDragExit,
	CdkDragMove,
	CdkDragRelease,
	CdkDragStart,
} from '@angular/cdk/drag-drop'

export type {
	FindOptionsSelect,
	FindOptionsWhere,
	FindOptionsRelations,
	FindOptionsOrder,
} from 'typeorm'
export type { Request, Response } from 'express'

export * from './chat/chat-message.js'

export interface FindManyOptions<T extends EntityCommon> {
	/**
	 * Specifies what columns should be retrieved.
	 */
	select?: FindOptionsSelect<EntityRecord<T>>

	/**
	 * Simple condition that should be applied to match entities.
	 */
	where?:
		| FindOptionsWhere<EntityRecord<T>>[]
		| FindOptionsWhere<EntityRecord<T>>

	/**
	 * Indicates what relations of entity should be loaded (simplified left join form).
	 */
	relations?: FindOptionsRelations<EntityRecord<T>>

	/**
	 * Specifies how relations must be loaded - using "joins" or separate queries.
	 * If you are loading too much data with nested joins it's better to load relations
	 * using separate queries.
	 *
	 * Default strategy is "join", but default can be customized in connection options.
	 */
	// relationLoadStrategy?: "join" | "query"

	/**
	 * Order, in which entities should be ordered.
	 */
	order?: FindOptionsOrder<EntityRecord<T>>

	/**
	 * Enables or disables query result caching.
	 */
	cache?: boolean | number | { id: any; milliseconds: number }

	/**
	 * Indicates what locking mode should be used.
	 *
	 * Note: For lock tables, you must specify the table names and not the relation names
	 */
	lock?:
		| { mode: 'optimistic'; version: number | Date }
		| {
				mode:
					| 'pessimistic_read'
					| 'pessimistic_write'
					| 'dirty_read'
					| 'pessimistic_partial_write'
					| 'pessimistic_write_or_fail'
					| 'for_no_key_update'
					| 'for_key_share'
				tables?: string[]
		  }

	/**
	 * Indicates if soft-deleted rows should be included in entity result.
	 */
	// withDeleted?: boolean

	/**
	 * If sets to true then loads all relation ids of the entity and maps them into relation values (not relation objects).
	 * If array of strings is given then loads only relation ids of the given properties.
	 */
	// loadRelationIds?:
	// | boolean
	// | { relations?: string[]; disableMixedMap?: boolean } // todo: extract options into separate interface, reuse

	/**
	 * Indicates if eager relations should be loaded or not.
	 * By default, they are loaded when find methods are used.
	 */
	// loadEagerRelations?: boolean

	/**
	 * If this is set to true, SELECT query in a `find` method will be executed in a transaction.
	 */
	transaction?: boolean

	/**
	 * Offset (paginated) where from entities should be taken.
	 */
	skip?: number

	/**
	 * Limit (paginated) - max number of entities should be taken.
	 */
	take?: number
}

export type FindManyPagedOptions<T extends EntityCommon> = Omit<
	FindManyOptions<T>,
	'take' | 'skip'
>

export interface PagingDefinition {
	pageSize: number
	pageIndex: number
}
export interface SortingDefinition<K extends string> {
	by: K
	direction?: 'asc' | 'desc'
}
export interface PagedData<T> extends PagingDefinition {
	list: T[]
	length: number
	pageStartIndex: number
	pageEndIndex: number
}

export const allowedAggregationFunctions = [
	'count',
	'sum',
	'min',
	'max',
	'avg',
] as const
export interface AggregationInput<
	T extends EntityInstance,
	A extends (typeof allowedAggregationFunctions)[number],
	K extends keyof T,
	O extends string,
> {
	aggregationFunction: A
	column: '*' | K
	outputName: O
}
export type AggregationOutput<
	T extends EntityInstance,
	A extends (typeof allowedAggregationFunctions)[number],
	G extends keyof T,
	K extends keyof T,
	O extends string,
> = {
	[P in G]: T[P] // properties that the query was grouped by
} & {
	[P in O]: A extends 'count' ? number : T[K] // output property
}

export type GroupedCount<T extends EntityInstance, G extends keyof T> = {
	[P in G]: T[P]
} & {
	$count: number
}

export interface ReportOptions extends ReportView, PagingDefinition {}

export interface ReportResult<T = any> extends PagedData<T> {
	columns: { displayName: string; property: string }[]
}

export interface Form {
	reset(): void
	markAllAsTouched(): void
	markAsTouched(): void
	markAsPristine(): void
	markAllAsDirty(): void
	markAsDirty(): void
	markAsUntouched(): void
	get valid(): boolean
	get invalid(): boolean
	get pending(): boolean
	get disabled(): boolean
	get enabled(): boolean
	get errors(): ValidationErrors | null
	get pristine(): boolean
	get dirty(): boolean
	get touched(): boolean
	get untouched(): boolean
}

import type { User } from './auth/user.js'
export type { User } from './auth/user.js'
export type { UserRole } from './auth/user-role.js'
export type { UserPermission } from './auth/user-permission.js'
export type { ChartConfiguration } from 'chart.js'
import './charts/chart-types.js'

export type RemoveMethods<T extends object> = {
	[P in keyof T]: T[P] extends (...args: any[]) => any ? never : T[P]
}

export const LoginStages = [
	'credentials',
	'mfa',
	'passwordChangeNeeded',
	'successful',
] as const
export type LoginStage = (typeof LoginStages)[number]

export type ClientLogLevel = 'success' | 'error' | 'warning' | 'info'

export type ResourceAvailabilityOnClientType =
	| 'none'
	| 'downloadable'
	| 'builtin'
export type ServiceIntegrationAvailabilityOnClientType = 'none' | 'builtin'

export interface ResourceDefinition<
	EL extends ExecutionLocation,
	Avail extends Omit<ResourceAvailabilityOnClientType, 'none'>,
> {
	resourceType: ResourceType
	mimeType: string
	fileName: string
	relativeUrl: string
	absoluteUrl: string
	getDataBase64?: EL extends 'server'
		? () => Promise<string>
		: Avail extends 'builtin'
			? () => Promise<string>
			: never
	getDataBuffer?: EL extends 'server' ? () => Promise<Buffer> : never
	getDataString?: EL extends 'server'
		? () => Promise<string>
		: Avail extends 'builtin'
			? () => Promise<string>
			: never
}

export const AiTypes = [
	'AzureOpenAi',
	'Gemini',
	'Anthropic',
	'DeepSeek',
	'Ollama',
] as const
export type AiType = (typeof AiTypes)[number]

export interface Swimlane {
	id: string
	name: string
	assignee: User
}

export interface SwimlaneAssignment {
	users?: (User | UserIdType | string) | (User | UserIdType | string)[]
	groups?: string | string[]
	roles?: string | string[]
}

export interface ProcessScope<P = Record<string, any>> {
	process: P
	initiator: User | null
	startSignalName?: string
	startMessageName?: string
	startTimerActivityId?: string
	startMessageData?: any
	action?: string
}
export interface ProcessTokenScope<
	P = Record<string, any>,
	T = Record<string, any>,
> extends ProcessScope<P> {
	token: T
	action: string
	swimlane: Swimlane
}

export {
	UserTaskToken,
	UserTaskTokenDetails,
} from './process/user-task-token.js'

import type Imap from 'node-imap'
import type { ParsedMail } from 'mailparser'
import { ReportView } from './bos/report-view.js'
export type MailType = 'mail' | 'readReceipt' | 'nonDeliveryReport'
export interface MailBody extends ParsedMail {
	sequenceNumber: number
	statusCode: string | undefined
	mailType: MailType
}
export type Mail = MailBody &
	Imap.ImapMessageAttributes & {
		// from node-email-reply-parser (not exported)
		getFragments(): {
			getContent(): string
			isSignature(): boolean
			isQuoted(): boolean
			isHidden(): boolean
			isEmpty(): boolean
		}[]
		getVisibleText(options?: {
			/**
			 * When true, treats non-hidden fragments surrounded by hidden fragments as hidden.
			 * Default false.
			 */
			aggressive?: boolean
		}): string
		getHtmlWithHeaders(): string
	}

export interface ImageSelectorOutput {
	readonly dataUrl: string
	readonly base64: string
	readonly mimeType: string
	readonly arrayBuffer: ArrayBuffer
	readonly fileName: string
	readonly fileNameExtension: string
}

export interface UserIdentityTokenRecord {
	identityProviderId?: string
	accessToken: string
	scopes: string[]
}

export type NumberProperties<T> = {
	[K in keyof T]: T[K] extends number ? K : never
}
export type NumberPropertyKeys<T> = keyof NumberProperties<T>

export type StringProperties<T> = {
	[K in keyof T]: T[K] extends string ? K : never
}
export type StringPropertyKeys<T> = keyof StringProperties<T>

export type TypedProperties<T, U> = {
	[K in keyof T]: T[K] extends U ? K : never
}
export type TypedPropertyKeys<T, U> = keyof TypedProperties<T, U>

export type BinaryData = ArrayBuffer | Buffer | File | Blob | Uint8Array
export type BinaryOrBase64 = BinaryData | string
export interface BinaryFile<T = BinaryData> {
	data: T
	fileName?: string
	mimeType?: string
}
export type BinaryDataOrFile<T = BinaryData> = T | BinaryFile<T>
export type { MatChipInputEvent } from '@angular/material/chips'
export { UserRecord } from './auth/user-record.js'
